import { Box, Stack, FormControl, Select, MenuItem, Chip, alpha, useTheme, Typography, Divider } from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LuCircle, LuCheck, LuX } from "react-icons/lu";
import { LoginHistory as LH } from "../../../db_access/login_history";
import dayjs from "dayjs";
import { getOrgTokens } from "../../../theme/style/organizationPalette";

const LoginHistoryFilters = ({ filters, onFilterChange }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const tok = getOrgTokens(isDark);

    const [organizations, setOrganizations] = useState([]);
    const [users, setUsers] = useState([]);

    useEffect(() => {
        LH.get_organizations()
            .then(res => res.ok ? res.json() : [])
            .then(data => setOrganizations(data))
            .catch(err => console.error("Error fetching organizations:", err));
    }, []);

    useEffect(() => {
        if (filters.company_id) {
            LH.get_users_by_org(filters.company_id)
                .then(res => res.ok ? res.json() : [])
                .then(data => setUsers(data))
                .catch(err => console.error("Error fetching users:", err));
        } else {
            setUsers([]);
            if (filters.user_id) {
                onFilterChange({ ...filters, user_id: null });
            }
        }
    }, [filters.company_id]);

    const dateRangePresets = [
        { label: t("All Time"), value: "all" },
        { label: t("Today"), value: "today" },
        { label: t("Last 7 Days"), value: "7days" },
        { label: t("Last 30 Days"), value: "30days" },
        { label: t("Last 90 Days"), value: "90days" },
    ];

    const handleDateRangeChange = (preset) => {
        let start_date = null;
        let end_date = dayjs().format("YYYY-MM-DD 23:59:59");

        switch (preset) {
            case "today":
                start_date = dayjs().format("YYYY-MM-DD 00:00:00");
                break;
            case "7days":
                start_date = dayjs().subtract(7, 'days').format("YYYY-MM-DD 00:00:00");
                break;
            case "30days":
                start_date = dayjs().subtract(30, 'days').format("YYYY-MM-DD 00:00:00");
                break;
            case "90days":
                start_date = dayjs().subtract(90, 'days').format("YYYY-MM-DD 00:00:00");
                break;
            default:
                start_date = null;
                end_date = null;
        }

        onFilterChange({ ...filters, start_date, end_date, datePreset: preset });
    };

    const statusOptions = [
        { label: t("All"), value: "all" },
        { label: t("Success"), value: "success" },
        { label: t("Failed"), value: "failed" },
    ];

    const inputStyles = {
        height: 40,
        borderRadius: tok.radius.input,
        bgcolor: isDark ? alpha('#000', 0.2) : '#fff',
        "& .MuiSelect-select": {
            ...tok.gridCellText,
            py: 1,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tok.border.card
        }
    };

    const labelStyles = {
        ...tok.gridCellText,
        fontSize: '0.85rem',
        color: tok.text.muted,
        mb: 0.5,
        display: 'block',
        textAlign: 'left'
    };

    return (
        <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={labelStyles}>{t("Organization")}</Typography>
                    <FormControl fullWidth>
                        <Select
                            value={filters.company_id || ""}
                            onChange={(e) => onFilterChange({ ...filters, company_id: e.target.value || null, user_id: null })}
                            sx={inputStyles}
                            displayEmpty
                        >
                            <MenuItem value="" sx={tok.gridCellText}>{t("All Organizations")}</MenuItem>
                            {organizations.map(org => (
                                <MenuItem key={org.id} value={org.id} sx={tok.gridCellText}>
                                    {org.company_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ flex: 1 }}>
                    <Typography sx={labelStyles}>{t("User")}</Typography>
                    <FormControl fullWidth disabled={!filters.company_id}>
                        <Select
                            value={filters.user_id || ""}
                            onChange={(e) => onFilterChange({ ...filters, user_id: e.target.value || null })}
                            sx={inputStyles}
                            displayEmpty
                        >
                            <MenuItem value="" sx={tok.gridCellText}>{t("All Users")}</MenuItem>
                            {users.map(user => (
                                <MenuItem key={user.user_id} value={user.user_id} sx={tok.gridCellText}>
                                    {user.user_id} {user.name ? `(${user.name})` : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ flex: 1 }}>
                    <Typography sx={labelStyles}>{t("Status")}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ height: 40 }}>
                        {statusOptions.map(option => {
                            const isSelected = filters.status === option.value;
                            const activeColor = option.value === 'success' ? '#10b981' : option.value === 'failed' ? '#ef4444' : tok.bg.tabActive;
                            return (
                                <Chip
                                    key={option.value}
                                    label={option.label}
                                    onClick={() => onFilterChange({ ...filters, status: option.value })}
                                    sx={{
                                        height: 32,
                                        width: 80,
                                        borderRadius: tok.radius.tabItem,
                                        ...tok.gridCellText,
                                        fontSize: '0.8rem',
                                        border: '1px solid',
                                        bgcolor: isSelected ? alpha(activeColor, 0.1) : 'transparent',
                                        color: isSelected ? activeColor : tok.text.muted,
                                        borderColor: isSelected ? activeColor : tok.border.card,
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            bgcolor: alpha(activeColor, 0.05),
                                            borderColor: activeColor
                                        },
                                        "& .MuiChip-label": { px: 1 }
                                    }}
                                />
                            );
                        })}
                    </Stack>
                </Box>
            </Stack>

            <Divider sx={{ opacity: 0.5 }} />

            <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography sx={{ ...tok.gridCellText, fontSize: '0.85rem', color: tok.text.muted, mr: 1, textAlign: 'left' }}>
                    {t("Quick Range")}:
                </Typography>
                {dateRangePresets.map(preset => (
                    <Chip
                        key={preset.value}
                        label={preset.label}
                        onClick={() => handleDateRangeChange(preset.value)}
                        sx={{
                            height: 28,
                            px: 1,
                            borderRadius: tok.radius.tabItem,
                            ...tok.gridCellText,
                            fontSize: '0.75rem',
                            border: '1px solid',
                            bgcolor: filters.datePreset === preset.value ? alpha(tok.bg.tabActive, 0.1) : 'transparent',
                            color: filters.datePreset === preset.value ? tok.bg.tabActive : tok.text.muted,
                            borderColor: filters.datePreset === preset.value ? tok.bg.tabActive : tok.border.card,
                            '&:hover': {
                                bgcolor: alpha(tok.bg.tabActive, 0.05),
                                borderColor: tok.bg.tabActive
                            }
                        }}
                    />
                ))}
            </Stack>
        </Stack>
    );
};

export default LoginHistoryFilters;
